export interface FormulationType {
  id: string;
  key: string;
  label: string;
}

export interface FormulationTypeRequest {
  key: string;
  label: string;
}

export type FormulationTypePayload = FormulationTypeRequest;

/** Display label for a product's formulation, or null when none is recorded. */
export function formulationLabel(product: {
  formulation_type?: FormulationType | { label?: string; key?: string } | string | null;
}): string | null {
  const value = product?.formulation_type;
  if (!value) return null;
  if (typeof value === 'object') {
    return value.label || value.key || null;
  }
  return value;
}

const FORMULATION_BADGE_CLASSES: string[] = [
  'badge-soft-primary',
  'badge-soft-warning',
  'badge-soft-success',
  'badge-soft-purple',
  'badge-soft-info',
  'badge-soft-teal',
  'badge-soft-pink',
  'badge-soft-indigo',
  'badge-soft-danger',
  'badge-soft-secondary',
];

const KNOWN_FORMULATION_BADGES: Record<string, string> = {
  liquid: 'badge-soft-primary',
  powder: 'badge-soft-warning',
  granule: 'badge-soft-success',
  granules: 'badge-soft-success',
  gel: 'badge-soft-info',
  suspension: 'badge-soft-purple',
  tablet: 'badge-soft-teal',
  tablets: 'badge-soft-teal',
  emulsion: 'badge-soft-danger',
  ec: 'badge-soft-danger',
  sc: 'badge-soft-purple',
  sl: 'badge-soft-indigo',
  wp: 'badge-soft-warning',
  wg: 'badge-soft-success',
  sp: 'badge-soft-pink',
};

/**
 * Returns a distinct badge class for each formulation type so each type
 * stands out with a different color across data tables and product views.
 */
export function formulationBadgeClass(product: {
  formulation_type?: FormulationType | { label?: string; key?: string } | string | null;
}): string {
  const value = product?.formulation_type;
  if (!value) return 'badge-soft-primary';

  const raw = typeof value === 'object' ? value.label || value.key || '' : value;
  const normalized = raw.trim().toLowerCase();
  if (!normalized) return 'badge-soft-primary';

  if (KNOWN_FORMULATION_BADGES[normalized]) {
    return KNOWN_FORMULATION_BADGES[normalized];
  }

  // Deterministically map any dynamic formulation type to a palette color
  let hash = 0;
  for (let i = 0; i < normalized.length; i++) {
    hash = (hash << 5) - hash + normalized.charCodeAt(i);
    hash |= 0;
  }
  const index = Math.abs(hash) % FORMULATION_BADGE_CLASSES.length;
  return FORMULATION_BADGE_CLASSES[index];
}

export enum ProductUnit {
  GRAMS = 'gm',
  MILLILITERS = 'ml',
  KILOGRAM = 'kg',
  LITERS = 'ltr',
}

export interface Company {
  id: string;
  name: string;
  address: string;
}

export interface ProductCategoryObject {
  id: string;
  name: string;
  description: string;
}

export interface Unit {
  id: string;
  name: string;
  short_name: string;
}

export interface PackagingType {
  id: string;
  name: string;
  description: string;
}

export interface Product {
  id: string;
  name: string;
  contents: string;
  hsn_code: string | null;

  /** `null` on every product created before the field shipped: not recorded, not a value. */
  formulation_type: FormulationType | null;

  // Product category info
  product_category: ProductCategoryObject | null;

  // Product packaging details
  product_packaging: ProductPackaging[];

  // Company info
  company: Company | null;

  // Timestamps
  created_at: string;
  updated_at: string;
}

/**
 * Product write payload for create (POST) and update (PATCH) requests.
 * On write, formulation_type is the UUID string ID of the FormulationType.
 */
export interface ProductWritePayload {
  name: string;
  contents?: string;
  hsn_code?: string | null;
  /** UUID id of the formulation type */
  formulation_type: string | null;
  product_category: string | null;
  company?: string | null;
  image_url?: File | string | null;
}

export type ProductRequest = ProductWritePayload;
export type ProductCreatePayload = ProductWritePayload;
export type ProductUpdatePayload = Partial<ProductWritePayload>;
export type ProductWrite = ProductWritePayload;

export interface ProductPackaging {
  id: string;
  bill_of_material: BillOfMaterial | null;
  price_rate_incl_gst: string;
  packing_size: number;
  dealer_rate: string;
  image_url: string | null;
  gst_rate: string;
  mrp: string;
  nos: number;
  dealer_rate_incl_gst: string;

  unit: Unit;
  packaging_type: PackagingType | null;

  // timestamps
  created_at: string;
  updated_at: string;

  // reference to product (can be ID string or full Product object depending on context)
  product: string | Product;

  stock_available: string | null;
}

export interface BillOfMaterial {
  id: string;
  materials: Material[];
  created_at: string;
  updated_at: string;
  product: string;
}

export interface Material {
  id: string;
  raw_material: RawMaterial;
  unit_name: string;
  unit_short_name: string;
  created_at: string;
  updated_at: string;
  quantity_per_unit: number;
  bill_of_material: string;
  unit: string;
}

export interface RawMaterial {
  id: string;
  category_name: string;
  created_at: string;
  updated_at: string;
  name: string;
  code: string;
  min_stock_level: number;
  category: string;
}

export interface ProductNameList {
  id: string;
  name: string;
  product_category: ProductCategoryObject | null;
  company?: Company | null;
  formulation_type?: FormulationType | null;
}
